import { LinkedinScoreboard } from './LinkedinScoreboardView';
import { useProspectingAvailability } from './useProspectingAvailability';
import { copyMessageLabel, DRAFT_COPY_GROUP, ordinaryCopyChoices } from './copyMessages';
import { useState } from 'react';
import { Button, EmptyState, Input, Skeleton } from '../../components/ui';
import {
  activityFunnel,
  rangeDailyFromActivity,
  rangeReportFromActivity,
  useAllActivity,
} from './activityStats';
import { useAllMemberships, useCopySets, useLeadLists } from './hooks';
import { todayIso } from './trackerFormat';
import {
  emailSummary,
  leadChannel,
  leadsCsv,
  notionSummary,
  reportCsv,
  slackSummary,
} from './types';
import type { Lead, LeadStage } from './types';

function downloadCsv(filename: string, text: string) {
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

function daysAgoIso(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

type SummaryFormat = 'slack' | 'email' | 'notion';

const SUMMARY_FORMAT_LABELS: Record<SummaryFormat, string> = {
  slack: 'Slack',
  email: 'Email',
  notion: 'Notion',
};

/**
 * One facet of the Reports filter, as checkboxes rather than a select: the
 * spec asks for multi-select, and nothing selected reads as "all" without
 * needing an explicit All option to keep in sync.
 */
function FilterCheckGroup({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string;
  options: { value: string; label: string; disabled?: boolean }[];
  selected: Set<string>;
  onToggle: (value: string) => void;
}) {
  return (
    <fieldset className="filter-checkgroup">
      <legend>{label}</legend>
      {options.length === 0 ? (
        <p className="filter-checkgroup-empty">None yet</p>
      ) : (
        <div className="filter-checkgroup-options">
          {options.map((option) => (
            <label key={option.value} className="filter-checkgroup-option">
              <input
                type="checkbox"
                checked={selected.has(option.value)}
                disabled={option.disabled ?? false}
                onChange={() => onToggle(option.value)}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      )}
    </fieldset>
  );
}

export function ReportsView({ leads, stages }: { leads: Lead[]; stages: LeadStage[] }) {
  const { message_enabled } = useProspectingAvailability();
  const reportCopySetsQuery = useCopySets();
  const membershipsQuery = useAllMemberships();
  const listsQuery = useLeadLists();
  const [from, setFrom] = useState(() => daysAgoIso(30));
  const [to, setTo] = useState(todayIso);
  const [copied, setCopied] = useState(false);
  const [summaryFormat, setSummaryFormat] = useState<SummaryFormat>('slack');
  // Multi-select (spec §Reports): an empty set means "no filter on this
  // facet", which keeps the all-leads default free of special cases.
  const [sources, setSources] = useState<Set<string>>(new Set());
  const [stageIds, setStageIds] = useState<Set<string>>(new Set());
  const [listIds, setListIds] = useState<Set<string>>(new Set());
  const [copySetIds, setCopySetIds] = useState<Set<string>>(new Set());
  const [channels, setChannels] = useState<Set<string>>(new Set());
  const [filtersOpen, setFiltersOpen] = useState(false);
  const activityQuery = useAllActivity();

  function toggleIn(set: Set<string>, apply: (next: Set<string>) => void, value: string) {
    const next = new Set(set);
    if (next.has(value)) {
      next.delete(value);
    } else {
      next.add(value);
    }
    apply(next);
  }

  if (membershipsQuery.isPending || activityQuery.isPending)
    return <Skeleton lines={4} label="Loading reports" />;

  const memberships = membershipsQuery.data ?? [];
  const lists = listsQuery.data ?? [];
  const copySets = reportCopySetsQuery.data ?? [];
  const rangeOk = from !== '' && to !== '' && from <= to;

  // Every number below is computed from this narrowed set, so the summary,
  // the chart, the funnel and both CSVs always describe the same slice.
  const listLeadIds =
    listIds.size === 0
      ? null
      : new Set(memberships.filter((m) => listIds.has(m.list_id)).map((m) => m.lead_id));
  // Channel is a fact about the copy set a lead was worked through, so a lead
  // counts as LinkedIn if any of its memberships used a LinkedIn set.
  const channelLeadIds =
    channels.size === 0
      ? null
      : new Set(
          memberships
            .filter((m) => {
              if (m.copy_set_id === null) {
                return false;
              }
              const set = copySets.find((entry) => entry.id === m.copy_set_id);
              return set !== undefined && channels.has(set.channel);
            })
            .map((m) => m.lead_id),
        );
  const copySetLeadIds =
    copySetIds.size === 0
      ? null
      : new Set(
          memberships
            .filter((m) => m.copy_set_id !== null && copySetIds.has(m.copy_set_id))
            .map((m) => m.lead_id),
        );

  const scopedLeads = leads.filter((lead) => {
    if (sources.size > 0) {
      // Three channels, from the one shared reading. Folding null into
      // outbound here meant every report scoped to Outbound silently carried
      // 668 leads whose channel nobody had ever recorded.
      if (!sources.has(leadChannel(lead))) {
        return false;
      }
    }
    if (stageIds.size > 0 && !stageIds.has(lead.stage_id)) {
      return false;
    }
    if (listLeadIds !== null && !listLeadIds.has(lead.id)) {
      return false;
    }
    if (copySetLeadIds !== null && !copySetLeadIds.has(lead.id)) {
      return false;
    }
    if (channelLeadIds !== null && !channelLeadIds.has(lead.id)) {
      return false;
    }
    return true;
  });
  const scopedIds = new Set(scopedLeads.map((lead) => lead.id));
  const scopedMemberships = memberships.filter((m) => scopedIds.has(m.lead_id));
  const activeFilters =
    (sources.size === 0 ? 0 : 1) +
    (stageIds.size === 0 ? 0 : 1) +
    (listIds.size === 0 ? 0 : 1) +
    (copySetIds.size === 0 ? 0 : 1) +
    (channels.size === 0 ? 0 : 1);

  // Batch 4: touches and replies come from the timeline, scoped to the same
  // slice of leads as everything else on this screen. Memberships still feed
  // the list and copy-set facets above — those are cycle facts.
  const scopedLeadIds = new Set(scopedLeads.map((lead) => lead.id));
  const scopedActivities = (activityQuery.data ?? []).filter((activity) =>
    scopedLeadIds.has(activity.lead_id),
  );
  const report = rangeOk
    ? rangeReportFromActivity(scopedLeads, scopedActivities, scopedMemberships, stages, from, to)
    : null;
  const daily = rangeOk
    ? rangeDailyFromActivity(scopedLeads, scopedActivities, scopedMemberships, from, to)
    : [];
  const funnel = activityFunnel(scopedLeads, scopedActivities, scopedMemberships, stages);
  const maxDailyTouches = Math.max(
    1,
    ...daily.map((row) => Math.max(row.touchesSent, row.replies)),
  );
  const summary =
    report === null
      ? ''
      : summaryFormat === 'slack'
        ? slackSummary(report)
        : summaryFormat === 'email'
          ? emailSummary(report)
          : notionSummary(report);

  function copySummary() {
    void navigator.clipboard.writeText(summary).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <div>
      <LinkedinScoreboard onDownload={downloadCsv} />
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          flexWrap: 'wrap',
          gap: 'var(--space-3)',
          marginBottom: 'var(--space-6)',
        }}
      >
        <Input label="From" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        <Input label="To" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        <Button
          variant={activeFilters > 0 ? 'primary' : 'secondary'}
          onClick={() => setFiltersOpen((open) => !open)}
          aria-expanded={filtersOpen}
        >
          {activeFilters === 0 ? 'Filters' : `Filters (${activeFilters})`}
        </Button>
        {activeFilters > 0 && (
          <Button
            variant="ghost"
            onClick={() => {
              setSources(new Set());
              setStageIds(new Set());
              setListIds(new Set());
              setCopySetIds(new Set());
              setChannels(new Set());
            }}
          >
            Clear
          </Button>
        )}
        <span style={{ flex: 1 }} />
        <Button
          variant="secondary"
          disabled={report === null}
          onClick={() => {
            if (report !== null) {
              downloadCsv(`sales-report-${from}-to-${to}.csv`, reportCsv(report, daily));
            }
          }}
        >
          Download report CSV
        </Button>
        <Button
          variant="secondary"
          onClick={() =>
            downloadCsv(
              activeFilters === 0 ? 'all-leads.csv' : 'leads-filtered.csv',
              leadsCsv(scopedLeads, stages, scopedMemberships, lists),
            )
          }
        >
          {activeFilters === 0 ? 'Export all leads CSV' : 'Export these leads CSV'}
        </Button>
      </div>

      {filtersOpen && (
        <div className="filter-popover" style={{ marginBottom: 'var(--space-6)' }}>
          <div className="filter-popover-grid">
            <FilterCheckGroup
              label="Source"
              options={[
                { value: 'outbound', label: 'Outbound' },
                { value: 'inbound', label: 'Inbound' },
                // Reachable on its own, so "how many have we never classified"
                // is a question the report can answer rather than a number
                // hidden inside Outbound.
                { value: 'not-set', label: 'Not set' },
              ]}
              selected={sources}
              onToggle={(value) => toggleIn(sources, setSources, value)}
            />
            <FilterCheckGroup
              label="Stage"
              options={[...stages]
                .sort((a, b) => a.position - b.position)
                .map((stage) => ({ value: stage.id, label: String(stage.name) }))}
              selected={stageIds}
              onToggle={(value) => toggleIn(stageIds, setStageIds, value)}
            />
            <FilterCheckGroup
              label="List"
              options={lists.map((list) => ({ value: list.id, label: list.name }))}
              selected={listIds}
              onToggle={(value) => toggleIn(listIds, setListIds, value)}
            />
            <FilterCheckGroup
              label="Copy set"
              options={(message_enabled ? ordinaryCopyChoices(copySets) : copySets).map((set) => ({
                value: set.id,
                label: message_enabled ? copyMessageLabel(set) : set.name,
              }))}
              selected={copySetIds}
              onToggle={(value) => toggleIn(copySetIds, setCopySetIds, value)}
            />
            {message_enabled && copySets.some((set) => set.review_state === 'draft') && (
              <FilterCheckGroup
                label={DRAFT_COPY_GROUP}
                options={copySets
                  .filter((set) => set.review_state === 'draft')
                  .map((set) => ({ value: set.id, label: copyMessageLabel(set), disabled: true }))}
                selected={copySetIds}
                onToggle={() => undefined}
              />
            )}
            <FilterCheckGroup
              label="Channel"
              options={[
                { value: 'email', label: 'Email' },
                { value: 'linkedin', label: 'LinkedIn' },
              ]}
              selected={channels}
              onToggle={(value) => toggleIn(channels, setChannels, value)}
            />
          </div>
          <p
            style={{
              margin: 'var(--space-3) 0 0',
              fontSize: 'var(--font-size-xs)',
              color: 'var(--text-muted)',
            }}
          >
            {scopedLeads.length === leads.length
              ? `Reporting on all ${leads.length} leads.`
              : `Reporting on ${scopedLeads.length} of ${leads.length} leads.`}
          </p>
        </div>
      )}

      {!rangeOk ? (
        <EmptyState title="Pick a date range" description="From must be on or before To." />
      ) : (
        report !== null && (
          <>
            {/* Range numbers */}
            <div className="tracker-dashboard-card" style={{ marginBottom: 'var(--space-6)' }}>
              <p className="tracker-dashboard-card-title">
                {from} → {to}
              </p>
              <div className="tracker-inbound-strip">
                <div className="tracker-inbound-stat">
                  <p className="tracker-inbound-value">{report.newLeads}</p>
                  <p className="tracker-inbound-label">New Leads</p>
                </div>
                <div className="tracker-inbound-stat">
                  <p className="tracker-inbound-value">{report.inbound}</p>
                  <p className="tracker-inbound-label">Inbound</p>
                </div>
                <div className="tracker-inbound-stat">
                  <p className="tracker-inbound-value">{report.touchesSent}</p>
                  <p className="tracker-inbound-label">Touches Sent</p>
                </div>
                <div className="tracker-inbound-stat">
                  <p className="tracker-inbound-value">{report.replies}</p>
                  <p className="tracker-inbound-label">Replies</p>
                </div>
                <div className="tracker-inbound-stat">
                  <p className="tracker-inbound-value">{report.replyRate.label}</p>
                  <p className="tracker-inbound-label">Reply Rate (All-Time)</p>
                </div>
              </div>
            </div>

            {/* Slack-ready summary */}
            <div className="tracker-dashboard-card" style={{ marginBottom: 'var(--space-6)' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 'var(--space-3)',
                }}
              >
                <p className="tracker-dashboard-card-title" style={{ marginBottom: 0 }}>
                  Shareable Summary
                </p>
                <span style={{ display: 'flex', gap: 'var(--space-2)' }}>
                  {(Object.keys(SUMMARY_FORMAT_LABELS) as SummaryFormat[]).map((format) => (
                    <Button
                      key={format}
                      variant={summaryFormat === format ? 'primary' : 'secondary'}
                      size="sm"
                      onClick={() => setSummaryFormat(format)}
                    >
                      {SUMMARY_FORMAT_LABELS[format]}
                    </Button>
                  ))}
                  <Button variant="secondary" size="sm" onClick={copySummary}>
                    {copied ? 'Copied ✓' : 'Copy'}
                  </Button>
                </span>
              </div>
              <pre
                style={{
                  margin: 'var(--space-4) 0 0',
                  padding: 'var(--space-4)',
                  background: 'var(--surface-sunken)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'inherit',
                }}
              >
                {summary}
              </pre>
            </div>

            {/* Velocity: touches and replies per day across the range */}
            <div className="tracker-dashboard-card" style={{ marginBottom: 'var(--space-6)' }}>
              <p className="tracker-dashboard-card-title">Velocity</p>
              {daily.every((row) => row.touchesSent === 0 && row.replies === 0) ? (
                <p
                  style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}
                >
                  No outreach activity in this range yet.
                </p>
              ) : (
                <>
                  <div
                    className="tracker-velocity"
                    role="img"
                    aria-label="Daily touches and replies"
                  >
                    {daily.map((row) => (
                      <div
                        key={row.date}
                        className="tracker-velocity-day"
                        title={`${row.date}: ${row.touchesSent} touches, ${row.replies} replies`}
                      >
                        <div className="tracker-velocity-bars">
                          <div
                            className="tracker-velocity-bar"
                            style={{
                              height: `${Math.round((row.touchesSent / maxDailyTouches) * 100)}%`,
                            }}
                          />
                          <div
                            className="tracker-velocity-bar tracker-velocity-bar--replies"
                            style={{
                              height: `${Math.round((row.replies / maxDailyTouches) * 100)}%`,
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="tracker-velocity-axis">
                    <span>{daily[0]?.date}</span>
                    <span className="tracker-velocity-legend">
                      <span className="tracker-velocity-swatch" /> touches
                      <span className="tracker-velocity-swatch tracker-velocity-swatch--replies" />{' '}
                      replies
                    </span>
                    <span>{daily[daily.length - 1]?.date}</span>
                  </div>
                </>
              )}
            </div>

            {/* Conversion funnel (all-time), alongside the range numbers */}
            <div className="tracker-dashboard-card" style={{ marginBottom: 'var(--space-6)' }}>
              <p className="tracker-dashboard-card-title">Conversion Funnel (All-Time)</p>
              <div className="tracker-funnel">
                {funnel.map((row) => (
                  <div key={row.label} className="tracker-funnel-row">
                    <span className="tracker-funnel-label" title={row.label}>
                      {row.label}
                    </span>
                    <div className="tracker-funnel-track">
                      <div
                        className="tracker-funnel-fill"
                        style={{
                          width: `${
                            (funnel[0]?.count ?? 0) === 0
                              ? 0
                              : Math.round((row.count / (funnel[0]?.count ?? 1)) * 100)
                          }%`,
                        }}
                      />
                    </div>
                    <span className="tracker-funnel-count">
                      {row.count}
                      {row.pctOfTouched === null ? '' : ` · ${row.pctOfTouched}%`}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Per-day table for the range */}
            <div className="tracker-dashboard-card">
              <p className="tracker-dashboard-card-title">Daily Breakdown</p>
              {/* The pane already sets both overflow axes; this only shortens
                  it. The header inside is sticky now — before, the column
                  labels scrolled away after about eight rows and never came
                  back, on the one table where every column is a bare number. */}
              <div className="tracker-table-scroll tracker-table-scroll--short">
                <table className="tracker-perf-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>New Leads</th>
                      <th>Touches Sent</th>
                      <th>Replies</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...daily].reverse().map((row) => (
                      <tr key={row.date}>
                        <td>{row.date}</td>
                        <td>{row.newLeads === 0 ? '—' : row.newLeads}</td>
                        <td>{row.touchesSent === 0 ? '—' : row.touchesSent}</td>
                        <td>{row.replies === 0 ? '—' : row.replies}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )
      )}
    </div>
  );
}

// ── Copy Library ──────────────────────────────────────────────────────────────
